import { LinksData } from '../models/StatsModel';
import {
  normalizeKeyword,
  removeHttpHttps,
  SELECTED_BRAND_COLOR,
} from '../../../../../../shared/utils/utils';
import { SOV_PIE_CHART_PALETTE } from '../../../../../../shared/utils/sovPalette';
import { PieChartData } from '../../../../../../shared/models/common/PieChartData';
import { MonitorType } from '../../../../../../shared/types/monitor/MonitorType';
import { DropdownItemData } from '../../../../../../shared/components/dropdown-item/DropdownItem';
import { t } from 'i18next';
import { KeywordResponse } from '../../../../../../shared/models/monitor/KeywordResponse';
import { TagsResponse } from '../../../../../../shared/models/tags/TagsResponse';
import { findTagIdInKeyword } from '../../../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../../../shared/helpers/tags/TagsHelper';

export const SOV_PIE_MAX_ITEMS = 15;

/**
 * Reguła "kto wchodzi na pie chart SoV, a kto do «inne»": sort malejąco po wartości, marka
 * forsowana ma gwarantowany slot, łącznie maxItems pozycji. Wydzielona, bo tej samej selekcji
 * używa selektor marek w Insights "Funnel vs competitors" — obie listy nie mogą się rozjechać.
 */
export const selectPieChartTopItems = (
  data: PieChartData[],
  forcedBrand: string,
  maxItems: number = SOV_PIE_MAX_ITEMS
): { topItems: PieChartData[]; itemsForOthers: PieChartData[] } => {
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const forcedBrandData = sortedData.find((item) => item.name === forcedBrand);

  if (forcedBrandData) {
    const dataWithoutForced = sortedData.filter((item) => item.name !== forcedBrand);
    return {
      topItems: [forcedBrandData, ...dataWithoutForced.slice(0, maxItems - 1)],
      itemsForOthers: dataWithoutForced.slice(maxItems - 1),
    };
  }
  return { topItems: sortedData.slice(0, maxItems), itemsForOthers: sortedData.slice(maxItems) };
};

export const getPieChartWithOthers = (
  shareOfVoiceChartData: PieChartData[],
  forcedBrand: string,
  keywordsMap: Map<string, string>,
  commonKeywords: KeywordResponse[] | null,
  tags: TagsResponse[] | null
): PieChartData[] => {
  const totalSum = shareOfVoiceChartData.reduce((acc, item) => acc + item.value, 0);
  const selection = selectPieChartTopItems(shareOfVoiceChartData, forcedBrand);
  const itemsForOthers = selection.itemsForOthers;

  let topItems = selection.topItems.map((item) => ({
    ...item,
    value: item.value,
  }));

  if (itemsForOthers.length > 0) {
    const othersSum = itemsForOthers.reduce((acc, item) => acc + item.value, 0);
    const othersPercentage = (othersSum / totalSum) * 100;
    const others = {
      name: t('others'),
      value: parseFloat(othersPercentage.toFixed(2)),
    };
    topItems = [...topItems, others];
  }

  topItems.forEach((item) => {
    if (item.name === t('others')) {
      return;
    }

    if (!tags || !commonKeywords) {
      item.name = keywordsMap.get(item.name) ?? item.name;
      return;
    }

    const tagId = findTagIdInKeyword(item.name, commonKeywords);
    const keyword = getTagLabel(tags, tagId, item.name);
    item.name = keywordsMap.get(keyword) ?? keyword;
  });

  const currentSum = topItems.reduce((acc, item) => acc + item.value, 0);

  if (currentSum < 100) {
    const missingPercentage = parseFloat((100 - currentSum).toFixed(2));
    const othersIndex = topItems.findIndex((item) => item.name === t('others'));

    if (othersIndex !== -1) {
      topItems[othersIndex].value = parseFloat(
        (topItems[othersIndex].value + missingPercentage).toFixed(2)
      );
    } else {
      topItems.push({
        name: t('others'),
        value: missingPercentage,
      });
    }
  }

  return topItems;
};


export const prepareAndGetColorsForPieChart = (pieChartData: PieChartData[], firmName: string) => {
  const updatedColors = [...SOV_PIE_CHART_PALETTE];
  const selectedIndex = pieChartData.findIndex(
    (data) => normalizeKeyword(data.name).toLowerCase() === normalizeKeyword(firmName).toLowerCase()
  );

  if (selectedIndex !== -1) {
    updatedColors[selectedIndex] = SELECTED_BRAND_COLOR;
  }

  return pieChartData.map((data, index) => ({
    name: data.name,
    color: updatedColors[index % updatedColors.length],
  }));
};

export const getMonitorTypeIcon = (monitorType: MonitorType) => {
  switch (monitorType) {
    case 'GEMINI_API':
    case 'GEMINI_API_BROWSER':
    case 'GEMINI_SEARCH_BROWSER':
      return 'ico--gemini-bck';

    case 'GPT':
    case 'GPT_API':
    case 'GPT_API_BROWSER':
    case 'GPT_SEARCH_BROWSER':
    case 'GPT_API_SEARCH_WEB':
      return 'ico--gpt-bck';

    case 'CLAUDE_API':
      return 'ico--claude-bck';

    case 'DEEP_SEEK_API':
      return 'ico--deepseek-bck';

    case 'PERPLEXITY_API':
    case 'PERPLEXITY_SEARCH_BROWSER':
      return 'ico--perplexity-bck';

    case 'COPILOT_SEARCH_BROWSER':
      return 'ico--copilot-bck';

    case 'GROK_SEARCH_BROWSER':
    case 'GROK_API':
      return 'ico--grok-bck';

    case 'AI_OVERVIEW_NEW':
      return 'ico--ai-overview-bck';

    case 'AI_MODE':
    case 'AI_OVERVIEW':
      return 'ico--ai-mode';

    default:
      return null;
  }
};

export const getDomainsOptions = (
  domains: string[],
  domain: string | null
): DropdownItemData<string>[] => {
  const baseOptions = (domains ?? []).map((d) => ({
    name: removeHttpHttps(d),
    type: d,
  }));

  const allOption = { name: t('allDomains'), type: '' };

  if (domain && !baseOptions.find((o) => o.name === domain) && domain !== allOption.name) {
    baseOptions.push({ name: domain, type: domain });
  }

  return [allOption, ...baseOptions];
};

export const findSelectedDomain = (
  domain: string | null,
  domainsOptions: DropdownItemData<string>[]
): DropdownItemData<string> => {
  if (!domain) {
    return domainsOptions[0];
  }

  return domainsOptions.find((o) => o.name === domain) || { name: domain, type: domain };
};

export const filterSourcesByDomain = (domain: string | null, sources: LinksData[]) => {
  if (!domain || domain === '') {
    return sources;
  }

  return sources.filter((s) => s.url.includes(domain));
};

// `withLabels = false` — nazwy takie, jak padły w odpowiedziach, bez etykiet marek (Explore AI Answers).
export const getKeywordMap = (
  keywordResponse: KeywordResponse[],
  withLabels = true
): Map<string, string> => {
  const keywordNameMap = new Map<string, string>();
  // Serwisy API zwracają obiekt błędu zamiast rzucać (`.catch((error) => error)`), więc przy 403/500
  // trafia tu coś, co nie jest tablicą — a `for...of` wywracał wtedy całą stronę.
  if (!Array.isArray(keywordResponse)) {
    return keywordNameMap;
  }

  for (const kw of keywordResponse) {
    keywordNameMap.set(kw.normalizedKeyword, (withLabels && kw.label) || kw.keyword);
  }

  return keywordNameMap;
};
